import React, { createContext, useContext, useState, ReactNode } from 'react';

// Locales are now embedded directly to avoid JSON import issues in the browser.
const en = {
  "appSubtitle": "Your one-stop solution for Document manipulation.",
  "allTools": "All Tools",
  "backToTools": "Back to Tools",
  "ariaBackToTools": "Back to all tools",
  "termsOfUse": "Terms of Use",
  "privacyPolicy": "Privacy Policy",
  "footerRights": "All rights reserved.",
  "toolMergeTitle": "Merge PDFs",
  "toolMergeDescription": "Combine multiple PDFs into one document.",
  "toolSplitTitle": "Split PDF",
  "toolSplitDescription": "Extract pages from a PDF into separate files.",
  "toolExtractTitle": "Extract Text",
  "toolExtractDescription": "Pull all text content from a PDF file.",
  "toolResumeTitle": "Summarize Document",
  "toolResumeDescription": "Generate a concise summary of a document.",
  "toolPdfToDocxTitle": "PDF to DOCX",
  "toolPdfToDocxDescription": "Convert a PDF file to an editable Word document.",
  "toolDocxToPdfTitle": "DOCX to PDF",
  "toolDocxToPdfDescription": "Convert a Word document to a PDF file.",
  "toolPptxToPdfTitle": "PPTX to PDF",
  "toolPptxToPdfDescription": "Convert a PowerPoint presentation to a PDF file.",
  "toolImageToPdfTitle": "Image to PDF",
  "toolImageToPdfDescription": "Convert JPG or PNG images into a PDF file.",
  "toolCompressTitle": "Compress PDF",
  "toolCompressDescription": "Reduce the file size of your PDF documents.",
  "toolProtectTitle": "Protect PDF",
  "toolProtectDescription": "Add a password and encrypt your PDF file.",
  "toolWatermarkTitle": "Add Watermark",
  "toolWatermarkDescription": "Stamp an image watermark onto your document.",
  "toolExtractImagesTitle": "Extract Images",
  "toolExtractImagesDescription": "Extract all images from a PDF or DOCX file.",
  "uploadTitle": "Drag & drop your files here",
  "uploadSubtitlePre": "or",
  "uploadSubtitleLink": "click to browse",
  "fileTypeDescriptionPdf": "PDF files only",
  "fileTypeDescriptionImage": "JPG or PNG files only",
  "fileTypeDescriptionAll": "PDF, DOC/DOCX, JPG/PNG files accepted",
  "fileTypeDescriptionAccepted": "Accepted",
  "fileTypeDescriptionGeneric": "Accepted file types",
  "selectedFiles": "Selected Files:",
  "statusUploading": "Uploading...",
  "statusUploadingProgress": "Uploading...",
  "statusProcessing": "Processing...",
  "statusGenerating": "Generating...",
  "errorUnexpected": "An unexpected error occurred.",
  "successTitle": "Success!",
  "mergeButton": "Merge PDFs",
  "errorMergeCount": "Please select at least two PDF files to merge.",
  "successMergeMessage": "Your PDF has been merged successfully.",
  "downloadMergedButton": "Download Merged PDF",
  "splitButton": "Split PDF",
  "errorSelectFileToSplit": "Please select a PDF file to split.",
  "successSplitMessage": "Your PDF has been split. The result is a ZIP file containing the individual pages.",
  "downloadZipButton": "Download ZIP File",
  "extractButton": "Extract Text",
  "errorSelectFileToExtract": "Please select a PDF file to extract text from.",
  "extractedTextLabel": "Extracted Text:",
  "copyButton": "Copy",
  "copiedButton": "Copied!",
  "noTextPlaceholder": "No text was extracted.",
  "resumeButton": "Summarize Document",
  "errorSelectFileToSummarize": "Please select a document to summarize.",
  "summaryLanguageLabel": "Summary Language",
  "langEnglish": "English",
  "langPortuguese": "Portuguese",
  "langSpanish": "Spanish",
  "summaryLabel": "Summary:",
  "originalFileLabel": "Original file:",
  "convertToDocxButton": "Convert to DOCX",
  "errorSelectFileToConvert": "Please select a PDF file to convert.",
  "successPdfToDocxMessage": "Your PDF has been converted to a DOCX file.",
  "downloadDocxButton": "Download DOCX File",
  "convertToPdfButton": "Convert to PDF",
  "errorSelectImageToConvert": "Please select at least one image file to convert.",
  "successImageToPdfMessage": "Your image(s) have been converted to a PDF file.",
  "downloadPdfButton": "Download PDF File",
  "errorSelectDocxToConvert": "Please select a DOCX file to convert.",
  "successDocxToPdfMessage": "Your DOCX file has been converted to a PDF.",
  "errorSelectPptxToConvert": "Please select a PPTX file to convert.",
  "successPptxToPdfMessage": "Your PPTX file has been converted to a PDF.",
  "extractImagesButton": "Extract Images",
  "errorSelectFileToExtractImages": "Please select a file to extract images from.",
  "successExtractImagesMessage": "Images have been extracted. The result is a ZIP file.",
  "compressButton": "Compress PDF",
  "errorSelectFileToCompress": "Please select a PDF file to compress.",
  "compressLevelTitle": "Compression Level",
  "compressLevelLowLabel": "Low Compression",
  "compressLevelLowDescription": "Higher quality, larger file size.",
  "compressLevelMediumLabel": "Medium Compression",
  "compressLevelMediumDescription": "Good balance between quality and size.",
  "compressLevelHighLabel": "High Compression",
  "compressLevelHighDescription": "Lower quality, smallest file size.",
  "successCompressMessage": "Your PDF has been compressed.",
  "downloadCompressedButton": "Download Compressed PDF",
  "protectButton": "Protect PDF",
  "errorSelectFileToProtect": "Please select a PDF file to protect.",
  "errorPasswordRequired": "Please enter a password.",
  "setPasswordLabel": "Set Password",
  "passwordPlaceholder": "Enter password to protect the file",
  "successProtectMessage": "Your PDF has been protected with a password.",
  "downloadProtectedButton": "Download Protected PDF",
  "watermarkButton": "Add Watermark",
  "errorSelectDocument": "Please select a document file.",
  "errorSelectWatermark": "Please select a watermark image file.",
  "watermarkUploadDoc": "1. Upload Document",
  "watermarkUploadWatermark": "2. Upload Watermark",
  "watermarkOptions": "Watermark Options",
  "watermarkPositionLabel": "Position",
  "watermarkSizeLabel": "Size:",
  "successWatermarkMessage": "The watermark has been added to your document.",
  "downloadWatermarkedButton": "Download Watermarked File",
  "termsTitle": "Terms of Use – DS Logic PDF Tools",
  "termsLastUpdated": "Last updated: November 11, 2025",
  "termsContent": [
    { "type": "p", "text": "Welcome to DS Logic PDF Tools!" },
    { "type": "p", "text": "By accessing or using our service, located at dslogic.com.br/pdf-tools (the \"Service\"), you (\"User\") agree to comply with and be bound by these Terms of Use." },
    { "type": "p", "text": "If you do not agree to these terms, please do not use the Service." },
    { "type": "h2", "text": "1. Service Description" },
    { "type": "p", "text": "The Service offers a set of online tools for manipulating PDF files (such as merging, splitting, compressing, etc.). The Service is provided \"AS IS\" and \"AS AVAILABLE\", without warranties of any kind." },
    { "type": "h2", "text": "2. Privacy and User Data" },
    { "type": "p", "text": "This is our most important policy. We respect your privacy." },
    {
      "type": "ul",
      "items": [
        "<strong>No Permanent Storage:</strong> We do not permanently store the files you upload or the resulting files generated by the Service.",
        "<strong>Automatic Deletion:</strong> Your files are processed on our servers and are automatically and permanently deleted after a short period (usually 1 hour) after processing or download is complete.",
        "<strong>No Claim of Ownership:</strong> DS Logic does not claim any ownership over the content or files you process through the Service. You retain full ownership of your files.",
        "<strong>No Monitoring:</strong> We do not monitor, view, or access your files, except as strictly necessary for the execution of the requested task (the automated processing)."
      ]
    },
    { "type": "h2", "text": "3. Conditions of Use" },
    { "type": "p", "text": "By using the Service, you agree not to:" },
    {
      "type": "ul",
      "items": [
        "Upload or process files containing viruses, malware, or any malicious code.",
        "Upload or process any material that is illegal, defamatory, infringes on third-party copyrights, or that you do not have the legal right to process.",
        "Attempt to reverse engineer, decompile, or intentionally overload the Service's servers."
      ]
    }
  ],
  "privacyTitle": "Privacy Policy – DS Logic PDF Tools",
  "privacyLastUpdated": "Last updated: November 11, 2025",
  "privacyContent": [
    { "type": "p", "text": "At DS Logic, we respect your privacy and are committed to protecting it. This Privacy Policy explains how we handle your files and data when you use our PDF Tools." },
    { "type": "h2", "text": "1. Data Collection and Use" },
    { "type": "p", "text": "Our most important policy is respect for your privacy." },
    {
      "type": "ul",
      "items": [
        "<strong>No Permanent Storage:</strong> We do not permanently store the files you upload or the resulting files generated by the Service.",
        "<strong>Automatic Deletion:</strong> Your files are processed on our servers and are automatically and permanently deleted after a short period (usually 1 hour) after processing or download is complete.",
        "<strong>No Claim of Ownership:</strong> DS Logic does not claim any ownership over the content or files you process through the Service. You retain full ownership of your files.",
        "<strong>No Monitoring:</strong> We do not monitor, view, or access your files, except as strictly necessary for the execution of the requested task (the automated processing)."
      ]
    },
    { "type": "h2", "text": "2. Security" },
    { "type": "p", "text": "We implement security measures to protect your files during processing. Communication with our servers is encrypted using HTTPS. After processing, your files are permanently deleted from our systems." },
    { "type": "h2", "text": "3. Information Sharing" },
    { "type": "p", "text": "We do not share, sell, rent, or trade your files or personal information with third parties." },
    { "type": "h2", "text": "4. Changes to this Policy" },
    { "type": "p", "text": "We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page." }
  ]
};

const pt = {
  "appSubtitle": "Sua solução completa para manipulação de documentos.",
  "allTools": "Todas as Ferramentas",
  "backToTools": "Voltar para Ferramentas",
  "ariaBackToTools": "Voltar para todas as ferramentas",
  "termsOfUse": "Termos de Uso",
  "privacyPolicy": "Política de Privacidade",
  "footerRights": "Todos os direitos reservados.",
  "toolMergeTitle": "Juntar PDFs",
  "toolMergeDescription": "Combine múltiplos PDFs em um único documento.",
  "toolSplitTitle": "Dividir PDF",
  "toolSplitDescription": "Extraia páginas de um PDF em arquivos separados.",
  "toolExtractTitle": "Extrair Texto",
  "toolExtractDescription": "Extraia todo o conteúdo de texto de um arquivo PDF.",
  "toolResumeTitle": "Resumir Documento",
  "toolResumeDescription": "Gere um resumo conciso de um documento.",
  "toolPdfToDocxTitle": "PDF para DOCX",
  "toolPdfToDocxDescription": "Converta um arquivo PDF para um documento Word editável.",
  "toolDocxToPdfTitle": "DOCX para PDF",
  "toolDocxToPdfDescription": "Converta um documento Word para um arquivo PDF.",
  "toolPptxToPdfTitle": "PPTX para PDF",
  "toolPptxToPdfDescription": "Converta uma apresentação PowerPoint para um arquivo PDF.",
  "toolImageToPdfTitle": "Imagem para PDF",
  "toolImageToPdfDescription": "Converta imagens JPG ou PNG em um arquivo PDF.",
  "toolCompressTitle": "Comprimir PDF",
  "toolCompressDescription": "Reduza o tamanho do arquivo dos seus documentos PDF.",
  "toolProtectTitle": "Proteger PDF",
  "toolProtectDescription": "Adicione uma senha e criptografe seu arquivo PDF.",
  "toolWatermarkTitle": "Adicionar Marca D'água",
  "toolWatermarkDescription": "Adicione uma imagem de marca d'água ao seu documento.",
  "toolExtractImagesTitle": "Extrair Imagens",
  "toolExtractImagesDescription": "Extraia todas as imagens de um arquivo PDF ou DOCX.",
  "uploadTitle": "Arraste e solte seus arquivos aqui",
  "uploadSubtitlePre": "ou",
  "uploadSubtitleLink": "clique para procurar",
  "fileTypeDescriptionPdf": "Apenas arquivos PDF",
  "fileTypeDescriptionImage": "Apenas arquivos JPG ou PNG",
  "fileTypeDescriptionAll": "Arquivos PDF, DOC/DOCX, JPG/PNG aceitos",
  "fileTypeDescriptionAccepted": "Aceitos",
  "fileTypeDescriptionGeneric": "Tipos de arquivo aceitos",
  "selectedFiles": "Arquivos Selecionados:",
  "statusUploading": "Enviando...",
  "statusUploadingProgress": "Enviando...",
  "statusProcessing": "Processando...",
  "statusGenerating": "Gerando...",
  "errorUnexpected": "Ocorreu um erro inesperado.",
  "successTitle": "Sucesso!",
  "mergeButton": "Juntar PDFs",
  "errorMergeCount": "Por favor, selecione pelo menos dois arquivos PDF para juntar.",
  "successMergeMessage": "Seu PDF foi juntado com sucesso.",
  "downloadMergedButton": "Baixar PDF Juntado",
  "splitButton": "Dividir PDF",
  "errorSelectFileToSplit": "Por favor, selecione um arquivo PDF para dividir.",
  "successSplitMessage": "Seu PDF foi dividido. O resultado é um arquivo ZIP contendo as páginas individuais.",
  "downloadZipButton": "Baixar Arquivo ZIP",
  "extractButton": "Extrair Texto",
  "errorSelectFileToExtract": "Por favor, selecione um arquivo PDF para extrair o texto.",
  "extractedTextLabel": "Texto Extraído:",
  "copyButton": "Copiar",
  "copiedButton": "Copiado!",
  "noTextPlaceholder": "Nenhum texto foi extraído.",
  "resumeButton": "Resumir Documento",
  "errorSelectFileToSummarize": "Por favor, selecione um documento para resumir.",
  "summaryLanguageLabel": "Idioma do Resumo",
  "langEnglish": "Inglês",
  "langPortuguese": "Português",
  "langSpanish": "Espanhol",
  "summaryLabel": "Resumo:",
  "originalFileLabel": "Arquivo original:",
  "convertToDocxButton": "Converter para DOCX",
  "errorSelectFileToConvert": "Por favor, selecione um arquivo PDF para converter.",
  "successPdfToDocxMessage": "Seu PDF foi convertido para um arquivo DOCX.",
  "downloadDocxButton": "Baixar Arquivo DOCX",
  "convertToPdfButton": "Converter para PDF",
  "errorSelectImageToConvert": "Por favor, selecione pelo menos uma imagem para converter.",
  "successImageToPdfMessage": "Sua(s) imagem(ns) foi(ram) convertida(s) para um arquivo PDF.",
  "downloadPdfButton": "Baixar Arquivo PDF",
  "errorSelectDocxToConvert": "Por favor, selecione um arquivo DOCX para converter.",
  "successDocxToPdfMessage": "Seu arquivo DOCX foi convertido para um PDF.",
  "errorSelectPptxToConvert": "Por favor, selecione um arquivo PPTX para converter.",
  "successPptxToPdfMessage": "Seu arquivo PPTX foi convertido para um PDF.",
  "extractImagesButton": "Extrair Imagens",
  "errorSelectFileToExtractImages": "Por favor, selecione um arquivo para extrair as imagens.",
  "successExtractImagesMessage": "As imagens foram extraídas. O resultado é um arquivo ZIP.",
  "compressButton": "Comprimir PDF",
  "errorSelectFileToCompress": "Por favor, selecione um arquivo PDF para comprimir.",
  "compressLevelTitle": "Nível de Compressão",
  "compressLevelLowLabel": "Compressão Baixa",
  "compressLevelLowDescription": "Qualidade maior, tamanho de arquivo maior.",
  "compressLevelMediumLabel": "Compressão Média",
  "compressLevelMediumDescription": "Bom equilíbrio entre qualidade e tamanho.",
  "compressLevelHighLabel": "Compressão Alta",
  "compressLevelHighDescription": "Qualidade menor, tamanho de arquivo menor.",
  "successCompressMessage": "Seu PDF foi comprimido.",
  "downloadCompressedButton": "Baixar PDF Comprimido",
  "protectButton": "Proteger PDF",
  "errorSelectFileToProtect": "Por favor, selecione um arquivo PDF para proteger.",
  "errorPasswordRequired": "Por favor, digite uma senha.",
  "setPasswordLabel": "Definir Senha",
  "passwordPlaceholder": "Digite a senha para proteger o arquivo",
  "successProtectMessage": "Seu PDF foi protegido com uma senha.",
  "downloadProtectedButton": "Baixar PDF Protegido",
  "watermarkButton": "Adicionar Marca D'água",
  "errorSelectDocument": "Por favor, selecione um arquivo de documento.",
  "errorSelectWatermark": "Por favor, selecione um arquivo de imagem para a marca d'água.",
  "watermarkUploadDoc": "1. Enviar Documento",
  "watermarkUploadWatermark": "2. Enviar Marca D'água",
  "watermarkOptions": "Opções da Marca D'água",
  "watermarkPositionLabel": "Posição",
  "watermarkSizeLabel": "Tamanho:",
  "successWatermarkMessage": "A marca d'água foi adicionada ao seu documento.",
  "downloadWatermarkedButton": "Baixar Arquivo com Marca D'água",
  "termsTitle": "Termos de Uso – DS Logic PDF Tools",
  "termsLastUpdated": "Última atualização: 11 de novembro de 2025",
  "termsContent": [
    { "type": "p", "text": "Bem-vindo às Ferramentas de PDF da DS Logic!" },
    { "type": "p", "text": "Ao acessar ou usar nosso serviço, localizado em dslogic.com.br/pdf-tools (o \"Serviço\"), você (\"Usuário\") concorda em cumprir e se vincular a estes Termos de Uso." },
    { "type": "p", "text": "Se você não concorda com estes termos, por favor, não utilize o Serviço." },
    { "type": "h2", "text": "1. Descrição do Serviço" },
    { "type": "p", "text": "O Serviço oferece um conjunto de ferramentas online para manipulação de arquivos PDF (como mesclar, dividir, comprimir, etc.). O Serviço é fornecido \"COMO ESTÁ\" e \"CONFORME DISPONÍVEL\", sem garantias de qualquer tipo." },
    { "type": "h2", "text": "2. Privacidade e Dados do Usuário" },
    { "type": "p", "text": "Esta é a nossa política mais importante. Nós respeitamos sua privacidade." },
    {
      "type": "ul",
      "items": [
        "<strong>Nenhum Armazenamento Permanente:</strong> Nós não armazenamos permanentemente os arquivos que você envia ou os arquivos resultantes gerados pelo Serviço.",
        "<strong>Exclusão Automática:</strong> Seus arquivos são processados em nossos servidores e automaticamente excluídos de forma permanente após um curto período de tempo (geralmente 1 hora) após o término do processamento ou do download.",
        "<strong>Não Reivindicamos Propriedade:</strong> A DS Logic não reivindica qualquer propriedade sobre o conteúdo ou os arquivos que você processa através do Serviço. Você mantém a propriedade total dos seus arquivos.",
        "<strong>Não Monitoramos:</strong> Não monitoramos, vemos ou acessamos seus arquivos, exceto quando estritamente necessário para a execução da tarefa solicitada (o processamento automático)."
      ]
    },
    { "type": "h2", "text": "3. Condições de Uso" },
    { "type": "p", "text": "Ao utilizar o Serviço, você concorda em não:" },
    {
      "type": "ul",
      "items": [
        "Enviar ou processar arquivos que contenham vírus, malware ou qualquer código malicioso.",
        "Enviar ou processar qualquer material que seja ilegal, difamatório, infrinja direitos autorais de terceiros ou que você não tenha o direito legal de processar.",
        "Tentar aplicar engenharia reversa, descompilar ou sobrecarregar intencionalmente os servidores do Serviço."
      ]
    }
  ],
  "privacyTitle": "Política de Privacidade – DS Logic PDF Tools",
  "privacyLastUpdated": "Última atualização: 11 de novembro de 2025",
  "privacyContent": [
    { "type": "p", "text": "Na DS Logic, respeitamos a sua privacidade e estamos empenhados em protegê-la. Esta Política de Privacidade explica como lidamos com os seus arquivos e dados ao utilizar as nossas Ferramentas de PDF." },
    { "type": "h2", "text": "1. Coleta e Uso de Dados" },
    { "type": "p", "text": "A nossa política mais importante é o respeito pela sua privacidade." },
    {
      "type": "ul",
      "items": [
        "<strong>Nenhum Armazenamento Permanente:</strong> Nós não armazenamos permanentemente os arquivos que você envia ou os arquivos resultantes gerados pelo Serviço.",
        "<strong>Exclusão Automática:</strong> Seus arquivos são processados em nossos servidores e automaticamente excluídos de forma permanente após um curto período de tempo (geralmente 1 hora) após o término do processamento ou do download.",
        "<strong>Não Reivindicamos Propriedade:</strong> A DS Logic não reivindica qualquer propriedade sobre o conteúdo ou os arquivos que você processa através do Serviço. Você mantém a propriedade total dos seus arquivos.",
        "<strong>Não Monitoramos:</strong> Não monitoramos, vemos ou acessamos seus arquivos, exceto quando estritamente necessário para a execução da tarefa solicitada (o processamento automático)."
      ]
    },
    { "type": "h2", "text": "2. Segurança" },
    { "type": "p", "text": "Implementamos medidas de segurança para proteger os seus arquivos durante o processamento. A comunicação com os nossos servidores é encriptada utilizando HTTPS. Após o processamento, os seus arquivos são eliminados permanentemente dos nossos sistemas." },
    { "type": "h2", "text": "3. Partilha de Informações" },
    { "type": "p", "text": "Não partilhamos, vendemos, alugamos ou trocamos os seus arquivos ou informações pessoais com terceiros." },
    { "type": "h2", "text": "4. Alterações a esta Política" },
    { "type": "p", "text": "Podemos atualizar esta Política de Privacidade de tempos em tempos. Iremos notificá-lo sobre quaisquer alterações, publicando a nova Política de Privacidade nesta página." }
  ]
};

const es = {
  "appSubtitle": "Su solución todo en uno para la manipulación de documentos.",
  "allTools": "Todas las Herramientas",
  "backToTools": "Volver a Herramientas",
  "ariaBackToTools": "Volver a todas las herramientas",
  "termsOfUse": "Términos de Uso",
  "privacyPolicy": "Política de Privacidad",
  "footerRights": "Todos los derechos reservados.",
  "toolMergeTitle": "Unir PDFs",
  "toolMergeDescription": "Combina múltiples PDFs en un solo documento.",
  "toolSplitTitle": "Dividir PDF",
  "toolSplitDescription": "Extrae páginas de un PDF en archivos separados.",
  "toolExtractTitle": "Extraer Texto",
  "toolExtractDescription": "Extrae todo el contenido de texto de un archivo PDF.",
  "toolResumeTitle": "Resumir Documento",
  "toolResumeDescription": "Genera un resumen conciso de un documento.",
  "toolPdfToDocxTitle": "PDF a DOCX",
  "toolPdfToDocxDescription": "Convierte un archivo PDF a un documento Word editable.",
  "toolDocxToPdfTitle": "DOCX a PDF",
  "toolDocxToPdfDescription": "Convierte un documento de Word a un archivo PDF.",
  "toolPptxToPdfTitle": "PPTX a PDF",
  "toolPptxToPdfDescription": "Convierte una presentación de PowerPoint a un archivo PDF.",
  "toolImageToPdfTitle": "Imagen a PDF",
  "toolImageToPdfDescription": "Convierte imágenes JPG o PNG en un archivo PDF.",
  "toolCompressTitle": "Comprimir PDF",
  "toolCompressDescription": "Reduce el tamaño de archivo de tus documentos PDF.",
  "toolProtectTitle": "Proteger PDF",
  "toolProtectDescription": "Añade una contraseña y encripta tu archivo PDF.",
  "toolWatermarkTitle": "Añadir Marca de Agua",
  "toolWatermarkDescription": "Añade una imagen de marca de agua a tu documento.",
  "toolExtractImagesTitle": "Extraer Imágenes",
  "toolExtractImagesDescription": "Extrae todas las imágenes de un archivo PDF o DOCX.",
  "uploadTitle": "Arrastra y suelta tus archivos aquí",
  "uploadSubtitlePre": "o",
  "uploadSubtitleLink": "haz clic para buscar",
  "fileTypeDescriptionPdf": "Solo archivos PDF",
  "fileTypeDescriptionImage": "Solo archivos JPG o PNG",
  "fileTypeDescriptionAll": "Se aceptan archivos PDF, DOC/DOCX, JPG/PNG",
  "fileTypeDescriptionAccepted": "Aceptados",
  "fileTypeDescriptionGeneric": "Tipos de archivo aceptados",
  "selectedFiles": "Archivos Seleccionados:",
  "statusUploading": "Subiendo...",
  "statusUploadingProgress": "Subiendo...",
  "statusProcessing": "Procesando...",
  "statusGenerating": "Generando...",
  "errorUnexpected": "Ocurrió un error inesperado.",
  "successTitle": "¡Éxito!",
  "mergeButton": "Unir PDFs",
  "errorMergeCount": "Por favor, selecciona al menos dos archivos PDF para unir.",
  "successMergeMessage": "Tu PDF ha sido unido exitosamente.",
  "downloadMergedButton": "Descargar PDF Unido",
  "splitButton": "Dividir PDF",
  "errorSelectFileToSplit": "Por favor, selecciona un archivo PDF para dividir.",
  "successSplitMessage": "Tu PDF ha sido dividido. El resultado es un archivo ZIP que contiene las páginas individuales.",
  "downloadZipButton": "Descargar Archivo ZIP",
  "extractButton": "Extraer Texto",
  "errorSelectFileToExtract": "Por favor, selecciona un archivo PDF para extraer el texto.",
  "extractedTextLabel": "Texto Extraído:",
  "copyButton": "Copiar",
  "copiedButton": "¡Copiado!",
  "noTextPlaceholder": "No se extrajo ningún texto.",
  "resumeButton": "Resumir Documento",
  "errorSelectFileToSummarize": "Por favor, selecciona un documento para resumir.",
  "summaryLanguageLabel": "Idioma del Resumen",
  "langEnglish": "Inglés",
  "langPortuguese": "Portugués",
  "langSpanish": "Español",
  "summaryLabel": "Resumen:",
  "originalFileLabel": "Archivo original:",
  "convertToDocxButton": "Convertir a DOCX",
  "errorSelectFileToConvert": "Por favor, selecciona un archivo PDF para convertir.",
  "successPdfToDocxMessage": "Tu PDF ha sido convertido a un archivo DOCX.",
  "downloadDocxButton": "Descargar Archivo DOCX",
  "convertToPdfButton": "Convertir a PDF",
  "errorSelectImageToConvert": "Por favor, selecciona al menos una imagen para convertir.",
  "successImageToPdfMessage": "Tu(s) imagen(es) ha(n) sido convertida(s) a un archivo PDF.",
  "downloadPdfButton": "Descargar Archivo PDF",
  "errorSelectDocxToConvert": "Por favor, seleccione un archivo DOCX para convertir.",
  "successDocxToPdfMessage": "Su archivo DOCX ha sido convertido a PDF.",
  "errorSelectPptxToConvert": "Por favor, seleccione un archivo PPTX para convertir.",
  "successPptxToPdfMessage": "Su archivo PPTX ha sido convertido a PDF.",
  "extractImagesButton": "Extraer Imágenes",
  "errorSelectFileToExtractImages": "Por favor, seleccione un archivo para extraer imágenes.",
  "successExtractImagesMessage": "Las imágenes han sido extraídas. El resultado es un archivo ZIP.",
  "compressButton": "Comprimir PDF",
  "errorSelectFileToCompress": "Por favor, selecciona un archivo PDF para comprimir.",
  "compressLevelTitle": "Nivel de Compresión",
  "compressLevelLowLabel": "Compresión Baja",
  "compressLevelLowDescription": "Mayor calidad, mayor tamaño de archivo.",
  "compressLevelMediumLabel": "Compresión Media",
  "compressLevelMediumDescription": "Buen equilibrio entre calidad y tamaño.",
  "compressLevelHighLabel": "Compresión Alta",
  "compressLevelHighDescription": "Menor calidad, menor tamaño de archivo.",
  "successCompressMessage": "Tu PDF ha sido comprimido.",
  "downloadCompressedButton": "Descargar PDF Comprimido",
  "protectButton": "Proteger PDF",
  "errorSelectFileToProtect": "Por favor, selecciona un archivo PDF para proteger.",
  "errorPasswordRequired": "Por favor, introduce una contraseña.",
  "setPasswordLabel": "Establecer Contraseña",
  "passwordPlaceholder": "Introduce la contraseña para proteger el archivo",
  "successProtectMessage": "Tu PDF ha sido protegido con una contraseña.",
  "downloadProtectedButton": "Descargar PDF Protegido",
  "watermarkButton": "Añadir Marca de Agua",
  "errorSelectDocument": "Por favor, selecciona un archivo de documento.",
  "errorSelectWatermark": "Por favor, selecciona un archivo de imagen para la marca de agua.",
  "watermarkUploadDoc": "1. Subir Documento",
  "watermarkUploadWatermark": "2. Subir Marca de Agua",
  "watermarkOptions": "Opciones de Marca de Agua",
  "watermarkPositionLabel": "Posición",
  "watermarkSizeLabel": "Tamaño:",
  "successWatermarkMessage": "La marca de agua ha sido añadida a tu documento.",
  "downloadWatermarkedButton": "Descargar Archivo con Marca de Agua",
  "termsTitle": "Términos de Uso – DS Logic PDF Tools",
  "termsLastUpdated": "Última actualización: 11 de noviembre de 2025",
  "termsContent": [
    { "type": "p", "text": "¡Bienvenido a las Herramientas de PDF de DS Logic!" },
    { "type": "p", "text": "Al acceder o utilizar nuestro servicio, ubicado en dslogic.com.br/pdf-tools (el \"Servicio\"), usted (\"Usuario\") acepta cumplir y estar sujeto a estos Términos de Uso." },
    { "type": "p", "text": "Si no está de acuerdo con estos términos, por favor, no utilice el Servicio." },
    { "type": "h2", "text": "1. Descripción del Servicio" },
    { "type": "p", "text": "El Servicio ofrece un conjunto de herramientas en línea para manipular archivos PDF (como unir, dividir, comprimir, etc.). El Servicio se proporciona \"TAL CUAL\" y \"SEGÚN DISPONIBILIDAD\", sin garantías de ningún tipo." },
    { "type": "h2", "text": "2. Privacidad y Datos del Usuario" },
    { "type": "p", "text": "Esta es nuestra política más importante. Respetamos su privacidad." },
    {
      "type": "ul",
      "items": [
        "<strong>Sin Almacenamiento Permanente:</strong> No almacenamos permanentemente los archivos que usted sube ni los archivos resultantes generados por el Servicio.",
        "<strong>Eliminación Automática:</strong> Sus archivos se procesan en nuestros servidores y se eliminan de forma automática y permanente después de un corto período de tiempo (generalmente 1 hora) después de que se complete el procesamiento o la descarga.",
        "<strong>Sin Reclamación de Propiedad:</strong> DS Logic no reclama ninguna propiedad sobre el contenido o los archivos que procesa a través del Servicio. Usted conserva la propiedad total de sus archivos.",
        "<strong>Sin Monitoreo:</strong> No monitoreamos, vemos ni accedemos a sus archivos, excepto cuando sea estrictamente necesario para la ejecución de la tarea solicitada (el procesamiento automatizado)."
      ]
    },
    { "type": "h2", "text": "3. Condiciones de Uso" },
    { "type": "p", "text": "Al utilizar el Servicio, usted se compromete a no:" },
    {
      "type": "ul",
      "items": [
        "Subir o procesar archivos que contengan virus, malware o cualquier código malicioso.",
        "Subir o procesar cualquier material que sea ilegal, difamatorio, que infrinja los derechos de autor de terceros o que no tenga el derecho legal de procesar.",
        "Intentar realizar ingeniería inversa, descompilar o sobrecargar intencionalmente los servidores del Servicio."
      ]
    }
  ],
  "privacyTitle": "Política de Privacidad – DS Logic PDF Tools",
  "privacyLastUpdated": "Última actualización: 11 de noviembre de 2025",
  "privacyContent": [
    { "type": "p", "text": "En DS Logic, respetamos su privacidad y estamos comprometidos a protegerla. Esta Política de Privacidad explica cómo manejamos sus archivos y datos cuando utiliza nuestras Herramientas de PDF." },
    { "type": "h2", "text": "1. Recopilación y Uso de Datos" },
    { "type": "p", "text": "Nuestra política más importante es el respeto por su privacidad." },
    {
      "type": "ul",
      "items": [
        "<strong>Sin Almacenamiento Permanente:</strong> No almacenamos permanentemente los archivos que usted sube ni los archivos resultantes generados por el Servicio.",
        "<strong>Eliminación Automática:</strong> Sus archivos se procesan en nuestros servidores y se eliminan de forma automática y permanente después de un corto período de tiempo (generalmente 1 hora) después de que se complete el procesamiento o la descarga.",
        "<strong>Sin Reclamación de Propiedad:</strong> DS Logic no reclama ninguna propiedad sobre el contenido o los archivos que procesa a través del Servicio. Usted conserva la propiedad total de sus archivos.",
        "<strong>Sin Monitoreo:</strong> No monitoreamos, vemos ni accedemos a sus archivos, excepto cuando sea estrictamente necesario para la ejecución de la tarea solicitada (el procesamiento automatizado)."
      ]
    },
    { "type": "h2", "text": "2. Seguridad" },
    { "type": "p", "text": "Implementamos medidas de seguridad para proteger sus archivos durante el procesamiento. La comunicación con nuestros servidores está encriptada mediante HTTPS. Después del procesamiento, sus archivos se eliminan permanentemente de nuestros sistemas." },
    { "type": "h2", "text": "3. Intercambio de Información" },
    { "type": "p", "text": "No compartimos, vendemos, alquilamos ni intercambiamos sus archivos o información personal con terceros." },
    { "type": "h2", "text": "4. Cambios en esta Política" },
    { "type": "p", "text": "Podemos actualizar esta Política de Privacidad de vez en cuando. Le notificaremos cualquier cambio publicando la nueva Política de Privacidad en esta página." }
  ]
};

type Translations = typeof en;
export type Locale = 'en' | 'pt' | 'es';

const translations: Record<Locale, Translations> = { en, pt, es };

const getInitialLocale = (): Locale => {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  const browserLang = navigator.language.split('-')[0];
  if (browserLang === 'pt') return 'pt';
  if (browserLang === 'es') return 'es';
  return 'en';
};

// This function allows for nested keys like 'parent.child'
const getNestedTranslation = (obj: any, key: string): any => {
  return key.split('.').reduce((o, i) => (o ? o[i] : undefined), obj);
};

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  // The 't' function can return a string or a structured object for policy pages
  t: (key: keyof Translations) => any;
}

const I18nContext = createContext(undefined as I18nContextType | undefined);

// FIX: Use React.PropsWithChildren to correctly type components that accept children via JSX.
export const I18nProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [locale, setLocale] = useState(getInitialLocale());

  const t = (key: keyof Translations): any => {
    const stringKey = String(key);
    // Fallback logic: current locale -> english -> key itself
    return getNestedTranslation(translations[locale], stringKey) || getNestedTranslation(translations['en'], stringKey) || stringKey;
  };

  return React.createElement(I18nContext.Provider, { value: { locale, setLocale, t } }, children);
};

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};